import React, { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { Virtuoso } from 'react-virtuoso';

import { getClass } from '../../../ship/utils/component-helpers';
import classNames from 'classnames';
import Checkbox from '../checkbox';
import { Search } from '../search';
import Button from '../button';

const DropdownMenu = (props: any) => {
  const {
    items,
    selected,
    onSearch,
    onSelect,
    menuAlign,
    isShowing,
    multi,
    searchable,
    confirmPrompt,
    onConfirm,
    onCancel,
    confirmText,
    cancelText,
    containerRef,
    matchToggleWidth,
    itemRenderer,
    virtualize,
    menuWidth,
    customPrompt,
    customPromptOnClick,
    selectAllPrompt,
    onSelectAll,
    overflowScroll,
  } = props;

  const menuRef = useRef();
  const [menuHeight, setMenuHeight] = useState(0);

  //  Menu height for top alignment
  useEffect(() => {
    if (isShowing && menuRef.current && menuAlign.includes('top')) {
      setMenuHeight(menuRef?.current['offsetHeight']);
    }
  }, [isShowing]);

  const onItemSelect = (value: any) => {
    onSelect(value);
  };

  if (!isShowing || !items) return null;

  const renderList = () => {
    const renderItem = (item: any) => {
      const { label, value, disabled } = item;
      const isSelected =
        multi && selected
          ? selected.map((s: any) => s.value).includes(value)
          : selected && selected.value === value;
      const itemClass = getClass(
        'dropdown-option flex items-center py-2 px-4 cursor-pointer text-base hover:bg-ice-100 hover:text-violet-500',
        { selected },
      );

      return (
        <div
          key={value}
          data-cy={`dropdown-option-${label}`}
          className={itemClass}
          onClick={() => onItemSelect({ label, value })}
        >
          {multi && (
            <div className="checkbox-container mr-3 flex items-center">
              <Checkbox checked={isSelected} />
            </div>
          )}
          {itemRenderer ? (
            itemRenderer({ label, value })
          ) : (
            <button
              data-cy="dropdown-menu-option-button"
              className={classNames(
                `text-deep-space-500 text-ellipsis whitespace-nowrap
                 disabled:text-neutral-gray-300 border-none cursor-pointer`,
                { 'flex gap-x-3': item.icon },
                { 'overflow-hidden': !overflowScroll },
              )}
              type="button"
              disabled={disabled}
            >
              <div>{label}</div>
              {item.icon && <div>{item.icon}</div>}
            </button>
          )}
        </div>
      );
    };

    if (virtualize) {
      return (
        <Virtuoso
          style={{ height: '270px' }}
          totalCount={items.length}
          itemContent={(i) => renderItem(items[i])}
        />
      );
    } else {
      return (
        <div
          className="item-list overflow-y-auto"
          style={{ maxHeight: '270px' }}
          ref={menuRef as any}
        >
          {items.map((item: any) => renderItem(item))}
        </div>
      );
    }
  };

  const content = (
    <div
      className={getClass(
        'dropdown-menu-container flex absolute flex-col justify-center rounded bg-white shadow-lg z-30',
        {
          'w-full': matchToggleWidth,
          'left-0': menuAlign.includes('left'),
          'right-0': menuAlign.includes('right'),
        },
      )}
      style={{
        top: menuAlign.includes('top') ? `-${menuHeight}px` : 'initial',
        width: (menuWidth
          ? `${menuWidth}px`
          : matchToggleWidth
          ? null
          : 'inherit') as any,
      }}
      ref={menuRef as any}
      data-cy="dropdown-menu-container"
    >
      {searchable && (
        <Search
          placeholder="Search..."
          debounce={true}
          styleType="borderless"
          onSearch={onSearch}
        />
      )}
      {selectAllPrompt && (
        <Button
          onClick={() =>
            onSelectAll(
              selected.length !== items.length ? 'Select All' : 'Deselect All',
            )
          }
          label={
            selected.length !== items.length ? 'Select All' : 'Deselect All'
          }
          size="small"
          kind="text"
          cypressSelector="select-all-btn"
        />
      )}
      {renderList()}
      {confirmPrompt && (
        <div className="flex flex-row items-center justify-end h-12 py-0 px-4">
          <Button
            onClick={onCancel}
            label={cancelText}
            size="small"
            kind="text"
            cypressSelector="cancel-btn"
          />
          <Button
            onClick={onConfirm}
            label={confirmText}
            size="small"
            kind="text"
            cypressSelector="submit-btn"
          />
        </div>
      )}
      {customPrompt && (
        <div
          className="border-t border-neutral-gray-500 flex items-center h-10"
          data-cy="custom-prompt-container"
        >
          <Button
            onClick={customPromptOnClick}
            label={customPrompt}
            size="small"
            kind="text"
          />
        </div>
      )}
    </div>
  );

  return ReactDOM.createPortal(content, containerRef.current);
};

DropdownMenu.propTypes = {
  selected: PropTypes.arrayOf(PropTypes.any),
  items: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.any }),
  ),
  onSelect: PropTypes.func,
  menuAlign: PropTypes.oneOf(['left', 'right', 'lefttop', 'righttop', 'top']),
  isShowing: PropTypes.bool,
  searchable: PropTypes.bool,
  multi: PropTypes.bool,
  confirmPrompt: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  onSearch: PropTypes.func,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  overflowScroll: PropTypes.bool,
  matchToggleWidth: PropTypes.bool,
  itemRenderer: PropTypes.func,
  virtualize: PropTypes.bool,
  menuWidth: PropTypes.number,
  customPrompt: PropTypes.string,
  customPromptOnClick: PropTypes.func,
};

DropdownMenu.defaultProps = {
  selected: [],
  onSelect: () => {},
  menuAlign: 'right',
  isShowing: false,
  searchable: false,
  confirmPrompt: false,
  onConfirm: () => {},
  onCancel: () => {},
  confirmText: 'Done',
  cancelText: 'Cancel',
  matchToggleWidth: false,
  itemRenderer: null,
  virtualize: false,
  menuWidth: null,
  customPrompt: '',
  customPromptOnClick: () => {},
  selectAllPrompt: false,
  onSelectAll: null,
  overflowScroll: false,
};

export default DropdownMenu;
